import { ConsoleLogger as Logger } from 'aws-amplify/utils';

import { CloudWatchLogsClient, CreateLogStreamCommand, DescribeLogStreamsCommand, type InputLogEvent, PutLogEventsCommand, ResourceAlreadyExistsException } from "@aws-sdk/client-cloudwatch-logs";
import { fromCognitoIdentityPool } from '@aws-sdk/credential-providers';

import { getIdToken, type User } from '@/auth';


const servicename = 'lantana-web';

const debugLevel = 'DEBUG';
const infoLevel = 'INFO';
const warnLevel = 'WARN';
const errorLevel = 'ERROR';

const defaultLogLevel = infoLevel;


export default class Log {
    private static instance: Log | null = null;

    public readonly ui: UILog;
    public readonly api: APILog;

    constructor(ui: UILog, api: APILog) {
        this.ui = ui;
        this.api = api;
    }

    static initialize(user: User, logLevel?: string): void {
        if (Log.instance) {
            return;
        }

        if (!logLevel) {
            logLevel = defaultLogLevel;
        }

        const userId = user.userId;
        const tenantId = user.tenantId;
        const sessionId = user.idToken.payload.jti ?? '';

        const adapter = new CloudWatchLogsAdapter(newCloudWatchLogsClient(), servicename, tenantId);

        const uiLogger = new Logger('UI', logLevel);
        uiLogger.addPluggable(adapter);

        const apiLogger = new Logger('API', logLevel);
        apiLogger.addPluggable(adapter);

        Log.instance = new Log(
            new UILog(uiLogger, userId, tenantId, sessionId),
            new APILog(apiLogger, userId, tenantId, sessionId)
        );
    }

    static getLogger(): Log {
        if (!Log.instance) {
            throw new Error('Not initialized.');
        }

        return Log.instance;
    }
}

/**
 * CloudWatchLogsAdapter はAmplifyからCloudWatchLogsへログイベントを送信するアダプタ
 *
 * NOTE: Amplify v6 が CloudWatchLogs をサポートしていないため独自実装を行っている。
 * 将来的にAmplifyでサポートされた場合には実装を切り替える
 *  - see: https://github.com/aws-amplify/amplify-js/issues/13336
 */
class CloudWatchLogsAdapter {
    private timestamp: string | undefined;
    private readonly client: CloudWatchLogsClient;
    private readonly servicename: string;
    private readonly tenantId: string;

    constructor(
        client: CloudWatchLogsClient,
        servicename: string,
        tenantId: string,
    ) {
        this.client = client;
        this.servicename = servicename;
        this.tenantId = tenantId;
    }

    getProviderName(): string {
        return '';
    }

    getCategoryName(): string {
        return 'Logging'; // addPluggable で登録する時の条件となる文字列
    }

    configure(config?: object): object {
        return config ?? {};
    }

    pushLogs(logEvents: InputLogEvent[]): void {
        (async () => {
            /*
             * 長時間ログインがされている場合に日付が跨る可能性がある。そのため都度ログストリーム名を生成している
             */

            const timestamp = getCurrentDate();

            const logGroupName = `/${this.servicename}`;
            const logStreamName = `${this.tenantId}/${timestamp}`;

            if (this.timestamp !== timestamp) {
                await this.createLogStream(logGroupName, logStreamName);
                this.timestamp = timestamp;
            }

            await this.client.send(new PutLogEventsCommand({
                logGroupName,
                logStreamName,
                logEvents,
            }));
        })().catch(err => {
            const c = window.console ?? (window as any).console_;
            c.log(err);
        });
    }

    /**
     * createLogStream は指定されたログストリームを作成する。既に存在している場合は正常終了となる。
     */
    private async createLogStream(logGroupName: string, logStreamName: string) {
        try {
            const streams = await this.client.send(new DescribeLogStreamsCommand({
                logGroupName: logGroupName,
                logStreamNamePrefix: logStreamName,
            }));

            const exists = streams.logStreams?.some(stream => stream.logStreamName === logStreamName);
            if (!exists) {
                await this.client.send(new CreateLogStreamCommand({
                    logGroupName: logGroupName,
                    logStreamName: logStreamName,
                }));
            }
        } catch (err) {
            if (err instanceof ResourceAlreadyExistsException) {
                // 他ユーザーから既に作成されている場合には何もしない
                return;
            }

            throw err;
        }
    }
}

/**
 * newCloudWatchLogsClient はIDトークンを利用して ClouWatchLogs へアクセスするクライアントを生成する
 *
 * クレデンシャルはその時点のIDトークンを利用して生成するため、クレデンシャルの期限が切れた場合には
 * 最新のIDトークンを利用して再度クレデンシャルを生成する。
 */
function newCloudWatchLogsClient() {
    const client = new CloudWatchLogsClient({
        region: import.meta.env.VITE_AWS_REGION,
        credentials: async () => fromCognitoIdentityPool({
            clientConfig: { region: import.meta.env.VITE_AWS_REGION },
            identityPoolId: import.meta.env.VITE_AWS_IDENTITY_POOL_ID as string,
            logins: {
                ['cognito-idp.ap-northeast-1.amazonaws.com/' + import.meta.env.VITE_AWS_USER_POOL_ID as string]: (await getIdToken()).toString(),
            },
        })(),
    });

    return client;
}

export class UILog {
    private logger: Logger;
    private userId: string;
    private tenantId: string;
    private sessionId: string;

    constructor(logger: Logger, userId: string, tenantId: string, sessionId: string) {
        this.logger = logger;
        this.userId = userId;
        this.tenantId = tenantId;
        this.sessionId = sessionId;
    }

    debug(module: string, trigger: string, message?: string) {
        if (!this.logger) {
            throw new Error('Logger is not initialized.');
        }

        if (!message) {
            message = '';
        }

        this.logger.debug(this.logMsg(debugLevel, module, trigger, message));
    }

    info(module: string, trigger: string, message?: string) {
        if (!this.logger) {
            throw new Error('Logger is not initialized.');
        }

        if (!message) {
            message = '';
        }

        this.logger.info(this.logMsg(infoLevel, module, trigger, message));
    }

    warn(module: string, trigger: string, message?: string) {
        if (!this.logger) {
            throw new Error('Logger is not initialized.');
        }

        if (!message) {
            message = '';
        }

        this.logger.warn(this.logMsg(warnLevel, module, trigger, message));
    }

    error(module: string, trigger: string, message: string) {
        if (!this.logger) {
            throw new Error('Logger is not initialized.');
        }

        this.logger.error(this.logMsg(errorLevel, module, trigger, message));
    }

    private logMsg(level: string, module: string, trigger: string, message: string): any {
        return JSON.stringify({
            level: level,
            time: getTimestamp(),
            userId: this.userId,
            tenantId: this.tenantId,
            sessionId: this.sessionId,
            module: module,
            trigger: trigger,
            message: message
        })
    }
}

export class APILog {
    private logger: Logger;
    private userId: string;
    private tenantId: string;
    private sessionId: string;

    constructor(logger: Logger, userId: string, tenantId: string, sessionId: string) {
        this.logger = logger;
        this.userId = userId;
        this.tenantId = tenantId;
        this.sessionId = sessionId;
    }

    debug(destination: string, message: string, ext?: any) {
        if (!this.logger) {
            throw new Error('Logger is not initialized.');
        }

        if (!message) {
            message = '';
        }

        this.logger.debug(this.logMsg(debugLevel, destination, message, ext));
    }

    info(destination: string, message: string, ext?: any) {
        if (!this.logger) {
            throw new Error('Logger is not initialized.');
        }

        if (!message) {
            message = '';
        }

        this.logger.info(this.logMsg(infoLevel, destination, message, ext));
    }

    warn(destination: string, message: string, ext?: any) {
        if (!this.logger) {
            throw new Error('Logger is not initialized.');
        }

        if (!message) {
            message = '';
        }

        this.logger.warn(this.logMsg(warnLevel, destination, message, ext));
    }

    error(destination: string, message: string, ext?: any) {
        if (!this.logger) {
            throw new Error('Logger is not initialized.');
        }

        if (!message) {
            message = '';
        }

        this.logger.error(this.logMsg(errorLevel, destination, message, ext));
    }

    private logMsg(level: string, destination: string, message: string, ext?: any): any {
        return JSON.stringify({
            level: level,
            time: getTimestamp(),
            userId: this.userId,
            tenantId: this.tenantId,
            sessionId: this.sessionId,
            destination: destination,
            message: message,
            ext: ext,
        })
    }
}

function getTimestamp(): string {
    const date = new Date();
    const isoString = date.toISOString();
    return isoString.slice(0, 23) + 'Z'; // ミリ秒を含むため、23文字まで取得しています
}

// ログストリーム名に使用する
function getCurrentDate(): string {
    const currentDate: Date = new Date();
    const year: number = currentDate.getUTCFullYear();
    const month: number = currentDate.getUTCMonth() + 1; // 0-indexed
    const day: number = currentDate.getUTCDate();
    const formattedDate: string = `${year}${month < 10 ? '0' : ''}${month}${day < 10 ? '0' : ''}${day}`;

    return formattedDate;
}
